'use client'

import React from 'react'
import Link from 'next/link'
import BlogPostLayout from '@/app/components/BlogPostLayout'
import { BlogTLDR, BlogKeyTakeaways, BlogFAQ, BlogSources, BlogLastUpdated, BlogComparisonTable, BlogAppCTA, BlogWhatTheAppChecks } from '@/app/components/blog/BlogAeo'
import CorrectionNote from '@/app/components/blog/CorrectionNote'

const link = 'text-[#1E6B47] hover:text-[#155235] underline font-medium'

export default function PostContent() {
  return (
    <BlogPostLayout
      title="Best Water Filter for Microplastics 2026: Only 6 of 13 Are Certified"
      author="Elise Maren"
      date="May 19, 2026"
      readTime="13 min read"
      heroImage="/blog/heroes/microplastics-water-filters-compared.webp"
      heroImageAlt="A countertop water filter pitcher beside a glass of clean water and a filter cartridge"
    >
      <BlogLastUpdated iso="2026-09-21" formatted="September 21, 2026" />

      <CorrectionNote date="September 21, 2026">
        This page previously said Clearly Filtered and Epic are certified to NSF 401 for microplastics and the Brita Elite is not; the reverse is true. It also said the APEC, Waterdrop and iSpring reverse-osmosis systems are certified to NSF 401 and remove 99%+ of microplastics &ldquo;including nanoplastics&rdquo;, that Berkey&apos;s elements are ceramic and independently tested to remove 99%+ of particles above 0.2 µm, and that three certifications cover microplastics. Only NSF/ANSI 401 does; of the RO systems only AquaTru holds it (96.48%); no certification covers nanoplastics; and Berkey says it has never tested its elements for microplastics. Our cost-per-litre figures did not add up either, and prices are now each maker&apos;s own.
      </CorrectionNote>

      <BlogTLDR>
        Buy a filter certified to <strong>NSF/ANSI 401 with a microplastics
        claim</strong>, the only certification that covers them. Of the 13 filters
        here, six hold it: the <strong>PUR PLUS</strong> (99.8%),{' '}
        <strong>Brita Elite</strong> (99.6%) and <strong>LifeStraw Home glass</strong>{' '}
        (at least 99%) pitchers, the <strong>Aquasana Clean Water Machine</strong>{' '}
        (99.6%), <strong>Multipure Aquaversa</strong>, and <strong>AquaTru</strong>{' '}
        countertop reverse osmosis (96.48%). The under-sink RO systems we list are
        not certified for microplastics, Berkey has never tested for them, and no
        certification covers nanoplastics.
      </BlogTLDR>

      <BlogKeyTakeaways
        items={[
          <>NSF/ANSI 401 is the only standard with a microplastics claim. It tests particles 0.5 to 1 µm and requires at least 85% reduction. Nothing certifies removal of nanoplastics below 0.5 µm.</>,
          <>The cheapest certified options are pitchers: PUR PLUS (99.8%), Brita Elite (99.6%), LifeStraw Home glass (at least 99%).</>,
          <>Of the reverse-osmosis systems here, only AquaTru is certified for microplastics. APEC and iSpring are certified for dissolved solids; Waterdrop for PFOA/PFOS, lead, fluoride and more, but not microplastics.</>,
          <>Berkey says it has never tested its elements for microplastics. EPA ordered Black Berkey sales stopped in 2022–23 over unregistered antimicrobial claims.</>,
          <>Clearly Filtered and Epic publish their own lab results but are not certified for microplastics. The standard Brita Classic is not certified for them either.</>,
        ]}
      />

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        The certification that matters
      </h2>
      <p>
        Filters are certified against NSF/ANSI standards by one of three bodies:
        NSF, the Water Quality Association (WQA) or IAPMO R&amp;T. They certify to
        the same standards, so any of the three counts. What matters is the claim
        on the listing:
      </p>
      <ul className="list-disc pl-6 space-y-2">
        <li><strong>NSF/ANSI 401</strong> is the only standard with a <strong>microplastics</strong> claim, tested on particles 0.5 to 1 µm with at least 85% reduction required. It also covers some pharmaceuticals and other emerging contaminants.</li>
        <li><strong>NSF/ANSI 42</strong> covers taste, odour and chlorine, and has a separate fine-particulate claim that is not a microplastics claim.</li>
        <li><strong>NSF/ANSI 53</strong> covers health contaminants such as lead, PFOA/PFOS and cysts. <strong>NSF/ANSI 58</strong> is the reverse-osmosis standard, for dissolved solids and a listed set of contaminants. Neither has a microplastics claim.</li>
      </ul>
      <p>
        So a filter can be &ldquo;NSF certified&rdquo; and still have no certified
        microplastic result. Look for &ldquo;microplastics&rdquo; on the 401 listing
        itself, on the certifier&apos;s database or the maker&apos;s performance data
        sheet.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        13 filters compared
      </h2>
      <BlogComparisonTable
        caption="Water filters by certified microplastic claim (listings and brand prices checked 21 September 2026)"
        columns={[
          { key: 'product', label: 'Filter' },
          { key: 'type', label: 'Type' },
          { key: 'mp', label: 'Microplastics' },
          { key: 'cert', label: 'Certifications' },
          { key: 'price', label: 'Price (brand site)' },
        ]}
        rows={[
          { product: 'PUR PLUS', type: 'Pitcher', mp: '99.8% certified (NSF 401)', cert: 'NSF 42, 53 (incl. total PFAS, lead), 401 (IAPMO)', price: 'Varies by bundle' },
          { product: 'Brita Elite', type: 'Pitcher cartridge', mp: '99.6% certified (NSF 401)', cert: 'NSF 42, 53, 401 (WQA)', price: 'Varies by bundle' },
          { product: 'LifeStraw Home glass 7-cup', type: 'Pitcher (membrane + carbon)', mp: 'At least 99% certified (NSF 401)', cert: 'NSF 42, 53, 401 (WQA)', price: '$64.95; filters $75.95 a year' },
          { product: 'Aquasana Clean Water Machine', type: 'Powered countertop dispenser', mp: '99.6% certified (NSF 401)', cert: 'NSF 42, 53 (incl. PFOA/PFOS), 401 (WQA)', price: '$199.99 sale ($399.99 list); filter 2-pack $73.49' },
          { product: 'Multipure Aquaversa', type: 'Carbon block system', mp: 'Certified (NSF 401 microplastics)', cert: 'NSF 42, 53, 401 (NSF)', price: 'See brand' },
          { product: 'AquaTru Classic', type: 'Countertop RO', mp: '96.48% certified (NSF 401)', cert: 'NSF 42, 53 (incl. PFOA/PFOS), 58, 372, 401 (IAPMO)', price: '$475; filters about $110–$130 a year' },
          { product: 'Waterdrop G3P800', type: 'Under-sink tankless RO', mp: 'Not certified for microplastics', cert: 'NSF 42, 53 (PFOA/PFOS), 58 (TDS, fluoride, lead and more), 372 (IAPMO)', price: '$999 list ($759 with code); filters $159.97 a year' },
          { product: 'APEC ROES-50', type: 'Under-sink RO', mp: 'Not certified for microplastics', cert: 'NSF 58, dissolved solids only (WQA)', price: '$230.99; filter set $79.99' },
          { product: 'iSpring RCC7AK', type: 'Under-sink RO', mp: 'Not certified for microplastics', cert: 'NSF 58, dissolved solids (NSF)', price: '$241.99; filters $80.99 a year' },
          { product: 'Clearly Filtered pitcher', type: 'Pitcher', mp: '>99.99% in the brand’s own lab tests; not certified', cert: 'NSF 42, 372', price: '$99; filters $58.50 per 100 gal' },
          { product: 'Epic Pure XP', type: 'Pitcher', mp: 'Lab-tested by the brand; not certified', cert: 'NSF 42 (material safety only), 372', price: '$84–$89; cartridges $54 per 100 gal' },
          { product: 'Big Berkey (Black Berkey elements)', type: 'Gravity-fed', mp: 'Never tested for microplastics, per Berkey', cert: 'None for Black Berkey; Phoenix elements NSF 42 (chlorine, taste, odour)', price: 'Not listed ("limited supply")' },
          { product: 'Brita Classic (Standard)', type: 'Pitcher', mp: 'Not certified for microplastics', cert: 'NSF 42; 53 for cadmium, copper, mercury (WQA)', price: 'Varies' },
        ]}
      />
      <p className="text-sm text-gray-500 italic">
        Certifications change. Check the model number on the NSF, WQA or IAPMO
        database, or the maker&apos;s performance data sheet, before you buy.
      </p>
      <p>
        Brand by brand:{' '}
        <Link href="/blog/do-brita-filters-remove-microplastics" className={link}>Brita</Link>,{' '}
        <Link href="/blog/does-aquatru-remove-microplastics" className={link}>AquaTru</Link>,{' '}
        <Link href="/blog/does-clearly-filtered-remove-microplastics" className={link}>Clearly Filtered</Link>{' '}
        and{' '}
        <Link href="/blog/does-berkey-filter-microplastics" className={link}>Berkey</Link>. Pitchers only:{' '}
        <Link href="/blog/best-water-filter-pitchers-ranked" className={link}>water filter pitchers ranked</Link>.
        Reverse osmosis only:{' '}
        <Link href="/blog/best-reverse-osmosis-systems-for-microplastics" className={link}>best reverse osmosis systems</Link>.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        How each type works
      </h2>
      <ul className="list-disc pl-6 space-y-2">
        <li>
          <strong>Dense carbon media</strong> (Brita Elite, Clearly Filtered, Epic,
          Multipure, Aquasana, each under its own brand name): activated carbon,
          often layered with other media, fine enough to trap small particles as
          well as adsorb chemicals such as chlorine.
        </li>
        <li>
          <strong>Granular carbon</strong> (standard Brita Classic, many fridge
          filters): loose carbon granules made for taste and chlorine, not for
          trapping fine particles. That is why the Classic is not certified for
          microplastics and the Elite, in the same pitcher, is.
        </li>
        <li>
          <strong>Membrane</strong> (LifeStraw): a microfilter membrane that blocks
          particles and bacteria, paired with carbon for taste and chemicals.
        </li>
        <li>
          <strong>Reverse osmosis</strong> (AquaTru, Waterdrop, APEC, iSpring):
          water is pushed through a membrane that rejects dissolved salts and
          metals. RO is the strongest option for dissolved contaminants, but only
          AquaTru here has a certified microplastic result, and it is lower than
          the best pitchers. AquaTru is rated 75% efficient; Waterdrop says 3:1
          pure to drain.
        </li>
        <li>
          <strong>Berkey</strong>: gravity-fed Black Berkey elements, which Berkey
          describes as &ldquo;a proprietary blend of media&rdquo; (not ceramic).
          Berkey publishes no pore size and says it has not tested them for
          microplastics.
        </li>
      </ul>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        What about nanoplastics?
      </h2>
      <p>
        A 2024 study in <em>PNAS</em> counted about <strong>240,000 plastic
        particles per litre of bottled water, around 90% of them nanoplastics</strong>{' '}
        smaller than 1 µm. No filter certification tests below 0.5 µm, so no filter
        can show certified nanoplastic removal. Claims that reverse osmosis removes
        &ldquo;everything, including nanoplastics&rdquo; are not backed by any
        certification, and the same PNAS team suggested that RO used in bottling
        may remove larger particles while leaving mostly nanoplastics behind. What
        you can buy with evidence is certified reduction of 0.5–1 µm particles.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        What else each filter is certified to remove
      </h2>
      <ul className="list-disc pl-6 space-y-2">
        <li><strong>AquaTru:</strong> the broadest list here, including lead (99.1%), fluoride (93.5%), pentavalent arsenic (98.0%), nitrate/nitrite (89.9%), PFOA/PFOS and several pharmaceuticals.</li>
        <li><strong>Waterdrop G3P800:</strong> PFOA/PFOS, lead, fluoride, cadmium, barium, radium and dissolved solids. Arsenic, nitrate and pharmaceuticals are tested by the brand, not certified.</li>
        <li><strong>APEC ROES-50 and iSpring RCC7AK:</strong> dissolved solids (TDS). Other claims are not certified for these models.</li>
        <li><strong>PUR PLUS:</strong> lead and total PFAS under NSF 53. <strong>Aquasana Clean Water Machine:</strong> PFOA/PFOS under NSF 53.</li>
        <li><strong>Berkey:</strong> Berkey publishes its own test reports for PFOA, heavy metals and more; none is a certification.</li>
        <li><strong>Brita Classic:</strong> chlorine and taste, plus cadmium, copper and mercury.</li>
      </ul>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Cost per litre
      </h2>
      <p>
        For two people drinking 2 litres a day (about 1,460 litres or 386 gallons
        a year), at each brand&apos;s own prices and filter ratings on 21 September
        2026:
      </p>
      <BlogComparisonTable
        caption="Cost per litre of filtered water, 1,460 L a year"
        columns={[
          { key: 'filter', label: 'Filter' },
          { key: 'y1', label: 'Year 1' },
          { key: 'after', label: 'After year 1' },
          { key: 'certified', label: 'Certified for microplastics?' },
        ]}
        rows={[
          { filter: 'Aquasana Clean Water Machine', y1: 'about $0.17/L', after: 'about $0.03/L', certified: 'Yes (99.6%)' },
          { filter: 'AquaTru Classic', y1: 'about $0.40/L', after: 'about $0.08/L', certified: 'Yes (96.48%)' },
          { filter: 'Clearly Filtered pitcher', y1: 'about $0.22/L', after: 'about $0.15/L', certified: 'No (lab-tested)' },
          { filter: 'Waterdrop G3P800', y1: 'about $0.63/L (with code)', after: 'about $0.11/L', certified: 'No' },
        ]}
      />
      <p>
        Pitcher costs depend on how many cartridges you get through: Clearly
        Filtered and Epic cartridges are rated for 100 gallons, which is three to
        four months for two people. Countertop units with long-life filters work
        out cheapest after the first year.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Maintenance
      </h2>
      <ul className="list-disc pl-6 space-y-2">
        <li><strong>Pitchers:</strong> no installation. Change the cartridge at its rated capacity; performance is only certified up to it.</li>
        <li><strong>AquaTru:</strong> pre/carbon filter every 6 months or 600 gallons, VOC filter every 12 months, RO membrane every 24 months or 1,200 gallons.</li>
        <li><strong>Under-sink RO (Waterdrop G3P800):</strong> carbon pre-filter every 6 months, carbon block every 12, RO membrane every 24. Needs space under the sink and a drain connection.</li>
        <li><strong>Berkey:</strong> scrub the Black Berkey elements with a stiff brush or Scotch-Brite pad when the flow slows; Berkey rates a pair at 6,000 gallons.</li>
        <li><strong>Whole-house systems</strong> usually run sediment and carbon stages that are not certified for microplastics. Check the listing, or add a certified filter at the kitchen tap.</li>
      </ul>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        What about just boiling it?
      </h2>
      <p>
        In hard water, boiling helps. A 2024 study in{' '}
        <em>Environmental Science &amp; Technology Letters</em> found that boiling
        water harder than 120 mg/L of calcium carbonate removed{' '}
        <strong>at least 80%</strong> of added polystyrene, polyethylene and
        polypropylene particles from 0.1 to 150 µm: the particles get trapped in
        the limescale that forms, which you then filter out. Soft water has little
        calcium to form scale, so the effect is much weaker there. More in{' '}
        <Link href="/blog/does-boiling-water-remove-microplastics" className={link}>
          does boiling water remove microplastics
        </Link>
        .
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Best filter by situation
      </h2>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">Cheapest certified option</h3>
      <p>
        <strong>PUR PLUS</strong>, or the <strong>Brita Elite</strong> cartridge if
        you already own a Brita pitcher. Both are certified under NSF 401 for
        microplastics (99.8% and 99.6%), with no installation. The{' '}
        <strong>LifeStraw Home glass</strong> pitcher is the certified option in
        glass.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">More water, no plumbing</h3>
      <p>
        <strong>Aquasana Clean Water Machine</strong>: a powered countertop
        dispenser certified to NSF 401 for microplastics (99.6%) and to NSF 53 for
        PFOA/PFOS. <strong>AquaTru Classic</strong> ($475) if you also want
        reverse osmosis for lead, fluoride, arsenic and nitrate: it has the
        broadest certified list here, including microplastics at 96.48%.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">Under the sink</h3>
      <p>
        None of the under-sink RO systems we checked is certified for
        microplastics. For a certified under-sink option, look at carbon block
        systems certified to NSF 401, such as the <strong>Multipure
        Aquaversa</strong>. Choose under-sink RO like the Waterdrop G3P800 for its
        certified PFOA/PFOS, lead and fluoride reduction, not for microplastics.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">Off-grid or no power</h3>
      <p>
        A certified pitcher (PUR PLUS, Brita Elite, LifeStraw Home glass) is
        gravity-fed and needs no power. We can&apos;t recommend Berkey for
        microplastics: Berkey says it has not tested its elements for them, and
        EPA ordered Black Berkey sales stopped in 2022 and 2023 because the
        elements were sold with antimicrobial claims without pesticide
        registration.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">Only chlorine and taste</h3>
      <p>
        A standard Brita Classic does that job, but it is not a microplastic filter.
      </p>

      <p>
        See also:{' '}
        <Link href="/blog/microplastics-in-tap-water" className={link}>
          microplastics in tap water
        </Link>
        .
      </p>

      <BlogWhatTheAppChecks
        items={[
          <>Bottled drink material. PET, HDPE, glass, aluminum.</>,
          <>Container condition signals from the photo.</>,
          <>Brand and SKU variant.</>,
          <>Use-context flags you log, heat, reuse, storage time.</>,
          <>Linked published research behind the 0–100 risk score.</>,
        ]}
      />

      <BlogAppCTA
        headline="Scan bottled drinks and water products before you buy"
        sub="Tap the barcode and snap a photo. The MicroPlastics app weighs packaging material + brand + condition and gives a 0–100 risk score with a safer swap."
        buttonLabel="Scan water products in the app"
      />

      <BlogFAQ
        items={[
          {
            question: 'What is the best water filter for microplastics?',
            answer:
              'One certified to NSF/ANSI 401 with a microplastics claim. In our comparison that is the PUR PLUS (99.8%), Brita Elite (99.6%) and LifeStraw Home glass (at least 99%) pitchers, the Aquasana Clean Water Machine (99.6%), the Multipure Aquaversa and the AquaTru countertop reverse-osmosis system (96.48%).',
          },
          {
            question: 'Does Brita remove microplastics?',
            answer:
              'The Brita Elite cartridge does: WQA certifies it to NSF/ANSI 401 for 99.6% reduction of microplastic particles 0.5 to 1 µm. The standard Brita Classic cartridge is not certified for microplastics; it is made for taste, chlorine and a few metals.',
          },
          {
            question: 'Does Berkey remove microplastics?',
            answer:
              'Nobody knows: Berkey says it has not tested its Black Berkey elements for microplastics and cannot make the claim, and they hold no certification from NSF, WQA or IAPMO. EPA ordered sales of Black Berkey products stopped in 2022 and 2023 over unregistered antimicrobial claims.',
          },
          {
            question: 'Does reverse osmosis remove microplastics?',
            answer:
              'It can, but check the model. Of the RO systems we checked, only the AquaTru Classic is certified for microplastics (96.48% under NSF/ANSI 401). The APEC ROES-50, iSpring RCC7AK and Waterdrop G3P800 hold other certifications but no microplastics claim.',
          },
          {
            question: 'Can any filter remove nanoplastics?',
            answer:
              'Not with certified evidence. NSF/ANSI 401 tests particles from 0.5 to 1 µm, and no certification covers smaller nanoplastics. The 2024 PNAS bottled-water study suggested reverse osmosis in bottling plants may leave mostly nanoplastics behind.',
          },
          {
            question: 'Which filter removes both microplastics and PFAS?',
            answer:
              'Among the filters we checked: AquaTru (NSF 401 microplastics, PFOA/PFOS under NSF 53), PUR PLUS (NSF 401 microplastics, total PFAS under NSF 53) and the Aquasana Clean Water Machine (NSF 401 microplastics, PFOA/PFOS under NSF 53).',
          },
          {
            question: 'Do refrigerator water filters remove microplastics?',
            answer:
              'Most are not certified for them, but some are: IAPMO lists Frigidaire PureSource Ultra II filters under NSF 401 with a microplastics claim. Check your filter’s model number on the NSF, WQA or IAPMO database.',
          },
          {
            question: 'Does boiling water remove microplastics?',
            answer:
              'In hard water, yes, largely. A 2024 study found boiling water harder than 120 mg/L of calcium carbonate removed at least 80% of added plastic particles, which get trapped in the limescale. Soft water has too little calcium for this to work well.',
          },
          {
            question: 'How often should I replace a microplastic water filter?',
            answer:
              'At the rated capacity, which is as far as the certification applies. Clearly Filtered and Epic cartridges are rated for 100 gallons (3–4 months for two people); AquaTru pre/carbon filters every 6 months, VOC filter every 12 and RO membrane every 24; Berkey rates a pair of Black Berkey elements at 6,000 gallons.',
          },
        ]}
      />

      <BlogSources
        items={[
          { authors: 'NSF', year: 2026, title: 'Water filter FAQs (NSF/ANSI 401 and microplastics)', publisher: 'NSF', url: 'https://www.nsf.org/about-nsf/faqs/water-filters-faqs' },
          { authors: 'AquaTru', year: 2026, title: 'AquaTru Classic performance data sheet (IAPMO R&T: NSF/ANSI 42, 53, 58, 372, 401)', publisher: 'AquaTru', url: 'https://aquatruwater.com/products/countertop-reverse-osmosis-water-purifier' },
          { authors: 'Aquasana', year: 2026, title: 'Clean Water Machine (AQ-CWM2) performance data', publisher: 'Aquasana', url: 'https://www.aquasana.com/on/demandware.static/-/Sites-aquasana-Library/default/dwd8f8ca33/PDF/AQ-CWM2_Performance_Data.pdf' },
          { authors: 'PUR', year: 2026, title: 'PUR PLUS pitcher filter performance data sheet', publisher: 'PUR', url: 'https://www.pur.com/wp-content/uploads/PUR_PDS_Pitcher_Filters_PPF951K.pdf' },
          { authors: 'Waterdrop', year: 2026, title: 'G3P800 specification sheet for reducing harmful substances', publisher: 'Waterdrop', url: 'https://cdn.shopify.com/s/files/1/0078/6156/7570/files/Specification_Sheet_for_Reducing_Harmful_Substances-G3P800.pdf?v=1725595808' },
          { authors: 'New Millennium Concepts (Berkey)', year: 2023, title: 'Do the Black Berkey elements remove plastic fibers from the water?', publisher: 'Berkey knowledge base', url: 'https://berkeywaterkb.com/do-the-black-berkey-purification-elements-remove-plastic-fibers-from-the-water/' },
          { authors: 'US Environmental Protection Agency, Region 8', year: 2022, title: 'Stop Sale, Use or Removal Order FIFRA-08-2023-0011 (Berkey Filters)', publisher: 'EPA', url: 'https://yosemite.epa.gov/oa/rhc/epaadmin.nsf/Filings/A56B5681D18012EF85258925007F0A58/$File/FIFRA-08-2023-0011%20BerkeyFilters%2012-2022.pdf' },
          { authors: 'Qian N, Gao X, Lang X, et al.', year: 2024, title: 'Rapid single-particle chemical imaging of nanoplastics by SRS microscopy', publisher: 'Proceedings of the National Academy of Sciences', url: 'https://www.pnas.org/doi/10.1073/pnas.2300582121' },
          { authors: 'Yu Z, Wang JJ, Liu LY, Li Z, Zeng EY', year: 2024, title: 'Drinking boiled tap water reduces human intake of nanoplastics and microplastics', publisher: 'Environmental Science & Technology Letters', url: 'https://doi.org/10.1021/acs.estlett.4c00081' },
        ]}
      />
    </BlogPostLayout>
  )
}
